
import React, { useState } from 'react';
import { ApprovalRequest } from './types';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from '@/components/ui/sonner';

interface ApprovalRequestsTabProps {
  approvalRequests: ApprovalRequest[];
  onApprove: (userId: string) => Promise<void>;
  onReject: (userId: string) => Promise<void>;
}

const ApprovalRequestsTab: React.FC<ApprovalRequestsTabProps> = ({ 
  approvalRequests,
  onApprove,
  onReject
}) => {
  const [processingUsers, setProcessingUsers] = useState<Set<string>>(new Set());
  
  // Ensure approvalRequests is always an array
  const safeRequests = Array.isArray(approvalRequests) ? approvalRequests : [];

  const handleApprove = async (userId: string) => {
    if (processingUsers.has(userId)) {
      console.log('Already processing approval for user:', userId);
      return;
    }
    
    console.log('Starting approval process for user:', userId);
    setProcessingUsers(prev => new Set(prev).add(userId));
    
    try {
      await onApprove(userId);
      console.log('Approval completed successfully for user:', userId);
    } catch (error) {
      console.error('Error in approval process for user:', userId, error);
      toast.error('Failed to approve user. Please try again.');
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleReject = async (userId: string) => {
    if (processingUsers.has(userId)) {
      console.log('Already processing rejection for user:', userId);
      return;
    }
    
    console.log('Starting rejection process for user:', userId);
    setProcessingUsers(prev => new Set(prev).add(userId));
    
    try {
      await onReject(userId);
      console.log('Rejection completed successfully for user:', userId);
    } catch (error) {
      console.error('Error in rejection process for user:', userId, error);
      toast.error('Failed to reject user. Please try again.');
    } finally {
      setProcessingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>User Approval Requests</CardTitle>
        <CardDescription>
          Review and approve new user sign-ups
        </CardDescription>
      </CardHeader>
      <CardContent>
        {safeRequests.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No pending approval requests</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium text-left">User</th>
                  <th className="px-4 py-3 font-medium text-left">Email</th>
                  <th className="px-4 py-3 font-medium text-left">Requested</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {safeRequests.map((request) => {
                  const isProcessing = processingUsers.has(request.user_id);
                  return (
                    <tr key={request.id} className="border-t hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="font-medium">
                          {request.user_name || "Unknown User"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ID: {request.user_id.slice(0, 8)}...
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {request.user_email || "No email"}
                      </td>
                      <td className="px-4 py-3">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => handleApprove(request.user_id)}
                            disabled={isProcessing}
                            className="min-w-[80px]"
                          >
                            {isProcessing ? 'Processing...' : 'Approve'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleReject(request.user_id)}
                            disabled={isProcessing}
                            className="min-w-[80px]"
                          >
                            {isProcessing ? 'Processing...' : 'Reject'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApprovalRequestsTab;
